import React, { useState } from 'react';
import { motion } from 'framer-motion';

import {
  Mail,
  MessageCircle,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  User,
  Building,
  MessageSquare,
  Calendar,
  Zap,
  Shield,
  Users,
  Globe,

} from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    services: [],
    budget: '',
    timeline: '',
    message: '',
    howDidYouHear: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState('');

  // Auto-select Product Inquiry if coming from products page
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('product') === 'true') {
      setFormData(prev => ({
        ...prev,
        services: ['product-inquiry']
      }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => {
      const services = prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId];
      return { ...prev, services };
    });

    if (formErrors.services) {
      setFormErrors(prev => ({ ...prev, services: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.company.trim()) errors.company = 'Company is required';
    if (formData.services.length === 0) errors.services = 'Please select at least one service';
    if (!formData.message.trim()) errors.message = 'Message is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormStatus('sending');

    // Format the message for WhatsApp
    const serviceNames = {
      'product-inquiry': 'Product Inquiry (QivraCampus / Plant Ticket)',
      'ai-ml': 'AI & Machine Learning Solutions',
      'cloud': 'Cloud & Digital Transformation',
      'software': 'Custom Software Development',
      'consulting': 'IT Consulting & Strategy',
      'cybersecurity': 'Cybersecurity Solutions',
      'other': 'Other / Multiple Services'
    };

    const budgetNames = {
      'under-50k': 'Under $50K',
      '50k-100k': '$50K - $100K',
      '100k-250k': '$100K - $250K',
      '250k-500k': '$250K - $500K',
      'over-500k': '$500K+',
      'discuss': "Let's Discuss"
    };

    const timelineNames = {
      'urgent': 'ASAP (< 1 month)',
      '1-3months': '1-3 months',
      '3-6months': '3-6 months',
      '6-12months': '6-12 months',
      'flexible': 'Flexible timing'
    };

    const selectedServices = formData.services.map(id => serviceNames[id]).join(', ');
    const budgetText = formData.budget ? budgetNames[formData.budget] : 'Not specified';
    const timelineText = formData.timeline ? timelineNames[formData.timeline] : 'Not specified';

    const whatsappMessage = `*New Contact Form Submission*

👤 *Name:* ${formData.name}
📧 *Email:* ${formData.email}
🏢 *Company:* ${formData.company}
${formData.phone ? `📱 *Phone:* ${formData.phone}` : ''}

🔧 *Services Interested In:*
${selectedServices}

💰 *Budget:* ${budgetText}
📅 *Timeline:* ${timelineText}

💬 *Message:*
${formData.message}
${formData.howDidYouHear ? `\n📢 *How they heard about us:* ${formData.howDidYouHear}` : ''}

---
Sent from Qivratech Contact Form`;

    // WhatsApp number
    const whatsappNumber = "917982426318";

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    // Open WhatsApp
    window.open(whatsappURL, '_blank');

    setFormStatus('success');
    setFormData({
      name: '', email: '', company: '', phone: '', services: [],
      budget: '', timeline: '', message: '', howDidYouHear: ''
    });

    // Reset success message after 5 seconds
    setTimeout(() => setFormStatus(''), 5000);

  };

  const services = [
    { id: 'product-inquiry', name: 'Product Inquiry (QivraCampus / Plant Ticket)', icon: '📦' },
    { id: 'ai-ml', name: 'AI & Machine Learning Solutions', icon: '🧠' },
    { id: 'cloud', name: 'Cloud & Digital Transformation', icon: '☁️' },
    { id: 'software', name: 'Custom Software Development', icon: '💻' },
    { id: 'consulting', name: 'IT Consulting & Strategy', icon: '⚡' },
    { id: 'cybersecurity', name: 'Cybersecurity Solutions', icon: '🛡️' },
    { id: 'other', name: 'Other / Multiple Services', icon: '🔧' }
  ];

  const budgetRanges = [
    { id: 'under-50k', name: 'Under $50K' },
    { id: '50k-100k', name: '$50K - $100K' },
    { id: '100k-250k', name: '$100K - $250K' },
    { id: '250k-500k', name: '$250K - $500K' },
    { id: 'over-500k', name: '$500K+' },
    { id: 'discuss', name: 'Let\'s Discuss' }
  ];

  const timelines = [
    { id: 'urgent', name: 'ASAP (< 1 month)' },
    { id: '1-3months', name: '1-3 months' },
    { id: '3-6months', name: '3-6 months' },
    { id: '6-12months', name: '6-12 months' },
    { id: 'flexible', name: 'Flexible timing' }
  ];

  const contactMethods = [
    {
      icon: <Mail className="w-8 h-8" />,
      title: 'Email Us',
      subtitle: 'Get in touch via email',
      value: 'contact@qivratech.com',
      action: 'mailto:contact@qivratech.com',
      color: 'from-cyan-400 to-blue-500'
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: 'WhatsApp',
      subtitle: 'Chat with our team',
      value: 'Start a Conversation',
      action: 'https://wa.me/917982426318',
      color: 'from-green-400 to-teal-500'
    }
  ];

  const offices = [
    {
      city: 'India',
      address: '170SF, SWARN NAGRI\nGreater Noida, Gautam Budh Nagar 201306',
      email: 'india@qivratech.com',
      isHQ: true
    },
    {
      city: 'Dubai',
      address: 'Central Park Towers DIFC\nFloor 21, Office 32-33\nDubai, UAE',
      email: 'dubai@qivratech.com',
      isHQ: false
    },
    {
      city: 'Norway',
      address: 'Dronning Eufemias gate 16, Oslo, Norway',
      email: 'norway@qivratech.com',
      isHQ: false
    }
  ];

  const faqs = [
    {
      question: "What's your typical project timeline?",
      answer: "Project timelines vary based on scope and complexity. Simple integrations can be completed in 2-4 weeks, while comprehensive AI platforms may take 3-6 months. We always provide detailed timelines during our initial consultation."
    },
    {
      question: "Do you work with startups or only enterprises?",
      answer: "We work with businesses of all sizes! From innovative startups to Fortune 500 companies, we tailor our solutions to fit your budget, timeline, and growth stage."
    },
    {
      question: "What industries do you specialize in?",
      answer: "We have deep expertise in healthcare, fintech, e-commerce, manufacturing, and SaaS. However, our AI and IT solutions are adaptable to virtually any industry."
    },
    {
      question: "Do you provide ongoing support and maintenance?",
      answer: "Yes! We offer comprehensive support packages including 24/7 monitoring, regular updates, performance optimization, and technical support to ensure your systems run smoothly."
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(0, 245, 255, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(123, 44, 191, 0.15) 0%, transparent 50%)
              `,
            }}
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-6">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wider">Let's Start Something Amazing</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extralight mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Get In Touch
              </span>
              <br />
              <span className="text-white">With Our Experts</span>
            </h1>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Ready to transform your business with AI and cutting-edge technology?
              Let's discuss your project and explore how we can help you achieve your goals.
            </p>
          </motion.div>

          {/* Quick Contact Methods */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid md:grid-cols-3 gap-6 mb-12 items-stretch"
          >
            {contactMethods.map((method, idx) => (
              <motion.a
                key={idx}
                href={method.action}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`h-full flex flex-col bg-gradient-to-br from-gray-900 to-black p-6 rounded-3xl border border-gray-800 hover:border-cyan-400/50 transition-all group`}
              >
                <div className={`text-cyan-400 mb-4 group-hover:scale-110 transition-transform`}>
                  {method.icon}
                </div>
                <h3 className="text-lg font-light text-white mb-2 group-hover:text-cyan-400 transition-colors">
                  {method.title}
                </h3>
                <p className="text-gray-400 text-sm mb-2">{method.subtitle}</p>
                <p className="text-cyan-400 font-semibold">{method.value}</p>
              </motion.a>
            ))}

            {/* Business Hours Card */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="h-full flex flex-col bg-gradient-to-br from-cyan-500/10 to-purple-600/10 p-6 rounded-3xl border border-cyan-400/30 transition-all"
            >
              <div className="text-cyan-400 mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-light text-white mb-4">
                Business Hours
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Monday - Friday:</span>
                  <span className="text-white">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Saturday:</span>
                  <span className="text-white">10:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Sunday:</span>
                  <span className="text-white">Closed</span>
                </div>
                <div className="mt-4 p-3 bg-cyan-500/20 rounded-lg">
                  <p className="text-cyan-300 text-xs font-medium">
                    ⚡ Emergency support available 24/7 for enterprise clients
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-3xl border border-gray-800"
              >
                <h2 className="text-3xl font-extralight text-white mb-2">Tell Us About Your Project</h2>
                <p className="text-gray-400 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-light mb-2">
                        Your Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full pl-12 pr-4 py-3 bg-gray-800 border-2 ${formErrors.name ? 'border-red-500' : 'border-gray-700'
                            } rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all`}
                          placeholder="Enter your full name"
                        />
                      </div>
                      {formErrors.name && <p className="mt-2 text-red-400 text-sm">{formErrors.name}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-light mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full pl-12 pr-4 py-3 bg-gray-800 border-2 ${formErrors.email ? 'border-red-500' : 'border-gray-700'
                            } rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all`}
                          placeholder="your.email@company.com"
                        />
                      </div>
                      {formErrors.email && <p className="mt-2 text-red-400 text-sm">{formErrors.email}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-light mb-2">
                        Company Name *
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className={`w-full pl-12 pr-4 py-3 bg-gray-800 border-2 ${formErrors.company ? 'border-red-500' : 'border-gray-700'
                            } rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all`}
                          placeholder="Your company name"
                        />
                      </div>
                      {formErrors.company && <p className="mt-2 text-red-400 text-sm">{formErrors.company}</p>}
                    </div>

                    <div>
                      <label className="block text-white font-light mb-2">
                        Phone Number
                      </label>
                      <div className="relative">
                        <MessageCircle className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-12 pr-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-light mb-2">
                      Services Interested In * <span className="text-gray-400 text-xs font-light">(Select multiple)</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {services.map((service) => (
                        <motion.div
                          key={service.id}
                          whileHover={{ scale: 1.02 }}
                          onClick={() => handleServiceToggle(service.id)}
                          className={`relative cursor-pointer p-3 rounded-xl border-2 transition-all ${formData.services.includes(service.id)
                            ? 'border-cyan-400 bg-cyan-400/10'
                            : 'border-gray-700 hover:border-gray-600'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-2xl mb-2">{service.icon}</div>
                            <div className="text-sm font-medium text-white">{service.name}</div>
                          </div>
                          {formData.services.includes(service.id) && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-cyan-400 rounded-full flex items-center justify-center">
                              <span className="text-black text-xs font-bold">✓</span>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                    {formErrors.services && <p className="mt-2 text-red-400 text-sm">{formErrors.services}</p>}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-light mb-2">
                        Project Budget
                      </label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-xl text-white focus:border-cyan-400 focus:outline-none transition-all"
                      >
                        <option value="">Select budget range</option>
                        {budgetRanges.map((range) => (
                          <option key={range.id} value={range.id}>{range.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-white font-light mb-2">
                        Timeline
                      </label>
                      <select
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-xl text-white focus:border-cyan-400 focus:outline-none transition-all"
                      >
                        <option value="">Select timeline</option>
                        {timelines.map((timeline) => (
                          <option key={timeline.id} value={timeline.id}>{timeline.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-light mb-2">
                      Project Details *
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className={`w-full pl-12 pr-4 py-3 bg-gray-800 border-2 ${formErrors.message ? 'border-red-500' : 'border-gray-700'
                          } rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all resize-none`}
                        placeholder="Tell us about your project, goals, and any specific requirements..."
                      />
                    </div>
                    {formErrors.message && <p className="mt-2 text-red-400 text-sm">{formErrors.message}</p>}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    whileHover={{ scale: formStatus === 'sending' ? 1 : 1.02 }}
                    whileTap={{ scale: formStatus === 'sending' ? 1 : 0.98 }}
                    className={`w-full bg-gradient-to-r from-cyan-400 to-purple-600 text-white px-8 py-4 rounded-full font-light text-lg hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center justify-center gap-2 ${formStatus === 'sending' ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {formStatus === 'sending' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending Message...
                      </>
                    ) : formStatus === 'success' ? (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Message Sent Successfully!
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </motion.button>

                  {formStatus === 'success' && (
                    <div className="bg-green-500/20 border border-green-500 text-green-300 p-4 rounded-xl text-center">
                      <p className="font-light">Thank you for your interest!</p>
                      <p className="text-sm">We'll get back to you within 24 hours to discuss your project.</p>
                    </div>
                  )}


                </form>
              </motion.div>
            </div>

            {/* Contact Information & Offices */}
            <div className="space-y-8">
              {/* Office Locations */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-3xl border border-gray-800"
              >
                <h3 className="text-xl font-light text-white mb-6 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-cyan-400" />
                  Our Offices
                </h3>
                <div className="space-y-6">
                  {offices.map((office, idx) => (
                    <div key={idx} className="pb-6 border-b border-gray-800 last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-light text-cyan-400">{office.city}</h4>
                        {office.isHQ && (
                          <span className="bg-gradient-to-r from-cyan-400 to-purple-600 text-white px-2 py-1 rounded-full text-xs font-light">
                            HQ
                          </span>
                        )}
                      </div>
                      <p className="text-gray-300 text-sm mb-3 whitespace-pre-line">{office.address}</p>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-400">📧 {office.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* FAQ */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-3xl border border-gray-800"
              >
                <h3 className="text-xl font-light text-white mb-6">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  {faqs.map((faq, idx) => (
                    <div key={idx} className="border-b border-gray-800 pb-4 last:border-b-0 last:pb-0">
                      <h4 className="text-white font-light mb-2">{faq.question}</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </motion.div>


            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-500/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-extralight text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's turn your vision into reality with cutting-edge AI and IT solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-400 to-purple-600 px-8 py-4 rounded-full font-light text-white hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center gap-2 justify-center"
              >
                <Calendar className="w-5 h-5" />
                Schedule Free Consultation
              </motion.button>
              <motion.a
                href="/projects"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-cyan-400 text-cyan-400 px-8 py-4 rounded-full font-light hover:bg-cyan-400 hover:text-black transition-all"
              >
                View Our Work
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;